/**
 * English Legal Translations
 * 法律文档翻译模块 - 英语
 * 
 * 包含隐私政策 (privacy) 和服务条款 (terms) 等法律文档的翻译
 */

import type { LegalTranslations } from '../../types';

export const enLegal: LegalTranslations = {
  privacy: {
    title: 'Privacy Policy',
    subtitle: 'How we collect, use, and protect your personal information.',
    lastUpdated: 'Last Updated',
    effectiveDate: 'Effective Date',
    meta: {
      lastUpdatedValue: 'February 6, 2026',
      effectiveDateValue: 'February 6, 2026',
    },
    introduction: 'Fujian Jinghui Environmental Technology Co., Ltd. ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.',
    sections: {
      infoCollect: {
        title: '1. Information We Collect',
        description: 'We may collect personal information that you voluntarily provide to us when you:',
        items: [
          'Register on our website or make an inquiry',
          'Request a quote or product information',
          'Subscribe to our newsletter',
          'Contact our sales or support team',
          'Participate in surveys or promotions',
        ],
      },
      typesCollected: {
        title: '2. Types of Information Collected',
        items: [
          'Personal Identification Information: Name, email address, phone number, company name, job title',
          'Demographic Information: Country, region, language preferences',
          'Technical Information: IP address, browser type, operating system, referring URLs',
          'Communication Data: Messages, inquiries, feedback, and support requests',
        ],
      },
      howUse: {
        title: '3. How We Use Your Information',
        description: 'We use the information we collect to:',
        items: [
          'Provide, maintain, and improve our products and services',
          'Respond to your inquiries and provide customer support',
          'Send you product information, quotes, and promotional materials',
          'Process transactions and send related information',
          'Monitor and analyze trends, usage, and activities',
          'Detect, investigate, and prevent fraudulent transactions',
          'Comply with legal obligations',
        ],
      },
      dataSecurity: {
        title: '4. Data Security',
        description: 'We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.',
        encryption: 'We use industry-standard encryption (SSL/TLS) to protect data transmitted between your browser and our servers.',
      },
      cookies: {
        title: '5. Cookies and Tracking Technologies',
        description: 'Our website may use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities. You can set your browser to refuse all cookies or to indicate when a cookie is being sent.',
      },
      thirdParty: {
        title: '6. Third-Party Disclosure',
        description: 'We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties except in the following circumstances:',
        items: [
          'With service providers who assist in our business operations',
          'When required by law, subpoena, or other legal process',
          'To protect our rights, privacy, safety, or property',
          'In connection with a merger, acquisition, or sale of assets',
        ],
      },
      dataRetention: {
        title: '7. Data Retention',
        description: 'We will retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.',
      },
      yourRights: {
        title: '8. Your Rights',
        description: 'You have the right to:',
        items: [
          'Access and receive a copy of your personal data',
          'Rectify inaccurate personal data',
          'Request deletion of your personal data',
          'Object to processing of your personal data',
          'Request restriction of processing',
          'Data portability',
        ],
      },
      children: {
        title: '9. Children\'s Privacy',
        description: 'Our website is not intended for children under the age of 16. We do not knowingly collect personal information from children under 16.',
      },
      international: {
        title: '10. International Data Transfers',
        description: 'Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from the laws of your country.',
      },
      changes: {
        title: '11. Changes to This Policy',
        description: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this policy.',
      },
      contactUs: {
        title: '12. Contact Us',
        description: 'If you have any questions about this Privacy Policy or our data practices, please contact us:',
        email: 'Email',
        address: 'Address',
        company: 'Fujian Jinghui Environmental Technology Co., Ltd.',
        addressLine: 'Building 25, Wanyang Science & Technology Park, 26 Tingwang Road, Tingjiang Town, Mawei District, Fuzhou, Fujian, China',
      },
    },
  },
  terms: {
    title: 'Terms of Service',
    subtitle: 'Please read these terms carefully before using our website and services.',
    lastUpdated: 'Last Updated',
    effectiveDate: 'Effective Date',
    meta: {
      lastUpdatedValue: 'February 6, 2026',
      effectiveDateValue: 'February 6, 2026',
    },
    introduction: 'Welcome to the website of Fujian Jinghui Environmental Technology Co., Ltd. By accessing or using this website, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any part of these terms, you may not use our website.',
    importantNotice: {
      title: 'Important Notice',
      description: 'By using this website, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you are using this website on behalf of a company or organization, you represent that you have the authority to bind such entity to these terms.',
    },
    sections: {
      useWebsite: {
        title: '1. Use of Website',
        description: 'This website is provided by Fujian Jinghui Environmental Technology Co., Ltd. for informational purposes only. By using this website, you agree to:',
        items: [
          'Use the website only for lawful purposes and in accordance with these Terms',
          'Not use the website in any way that could damage, disable, overburden, or impair it',
          'Not attempt to gain unauthorized access to any part of the website',
          'Not use the website to transmit any viruses, worms, or harmful code',
          'Respect the intellectual property rights of JH Heatsup and others',
        ],
      },
      intellectualProperty: {
        title: '2. Intellectual Property Rights',
        description: 'All content on this website, including but not limited to text, graphics, logos, images, audio clips, digital downloads, and software, is the property of Fujian Jinghui Environmental Technology Co., Ltd. or its content suppliers and is protected by international copyright, trademark, and other intellectual property laws.',
        restriction: 'You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, or exploit any content from this website without our prior written consent.',
      },
      productInfo: {
        title: '3. Product Information and Pricing',
        description: 'All products and services presented on this website are subject to availability. We reserve the right to discontinue any product or service at any time.',
        pricing: 'Prices and specifications are subject to change without notice. We make every effort to ensure the accuracy of product information, but errors may occur. We reserve the right to correct any errors, inaccuracies, or omissions and to change or update information at any time.',
        currency: 'All prices are quoted in USD unless otherwise specified. Shipping costs, taxes, and duties are additional and will be calculated during the ordering process.',
      },
      quotesOrders: {
        title: '4. Quotes and Orders',
        description: 'Quotes provided through this website are valid for 30 days unless otherwise specified. All orders are subject to acceptance by JH Heatsup.',
        contract: 'By submitting an order, you agree to purchase the products in accordance with our then-current terms, including pricing, shipping, and payment terms. A binding contract is formed only when we confirm acceptance of your order.',
      },
      warranties: {
        title: '5. Warranties and Disclaimers',
        description: 'THIS WEBSITE AND ALL CONTENT, PRODUCTS, AND SERVICES PROVIDED THROUGH IT ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.',
        disclaimer: 'JH Heatsup DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGION. We do not warrant that the website will be uninterrupted, timely, secure, or error-free.',
      },
      liability: {
        title: '6. Limitation of Liability',
        description: 'IN NO EVENT SHALL FUJIAN JINGHUI ENVIRONMENT TECHNOLOGY CO., LTD., ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THIS WEBSITE OR ANY PRODUCTS OR SERVICES PURCHASED FROM US.',
        limit: 'Our total liability for any claim arising out of or relating to these terms or our products and services shall not exceed the amount paid by you for the products or services giving rise to such claim.',
      },
      privacy: {
        title: '7. Privacy Policy',
        description: 'Your use of this website is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your information.',
      },
      thirdPartyLinks: {
        title: '8. Third-Party Links',
        description: 'This website may contain links to third-party websites that are not owned or controlled by JH Heatsup. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites. You access such links at your own risk.',
        recommendation: 'We encourage you to review the terms and privacy policy of any third-party website you visit.',
      },
      governingLaw: {
        title: '9. Governing Law and Disputes',
        description: 'These Terms shall be governed by and construed in accordance with the laws of the People\'s Republic of China, without regard to its conflict of law provisions.',
        disputes: 'Any dispute arising out of or relating to these Terms or your use of this website shall be resolved through good-faith negotiations between the parties. If such negotiations fail, the dispute shall be submitted to binding arbitration in accordance with the rules of the China International Economic and Trade Arbitration Commission.',
      },
      export: {
        title: '10. Export Controls',
        description: 'You agree to comply with all applicable export control laws and regulations. You represent that you are not located in, under the control of, or a national or resident of any country subject to comprehensive U.S. sanctions or embargoes.',
      },
      termination: {
        title: '11. Termination',
        description: 'We may terminate or suspend your access to this website immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use the website will cease immediately.',
      },
      changes: {
        title: '12. Changes to These Terms',
        description: 'We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide notice 30 days prior to any new terms taking effect. Your continued use of the website after such changes constitutes acceptance of the new Terms.',
      },
      severability: {
        title: '13. Severability',
        description: 'If any provision of these Terms is held to be invalid, illegal, or unenforceable, such provision shall be modified to the minimum extent necessary to make it enforceable, and the remaining provisions shall continue in full force and effect.',
      },
      contactUs: {
        title: '14. Contact Information',
        description: 'If you have any questions about these Terms of Service, please contact us:',
        email: 'Email',
        address: 'Address',
        company: 'Fujian Jinghui Environmental Technology Co., Ltd.',
        addressLine: 'Building 25, Wanyang Science & Technology Park, 26 Tingwang Road, Tingjiang Town, Mawei District, Fuzhou, Fujian, China',
      },
    },
  },
  installationGuide: {
    header: {
      title: 'Installation Guide',
      subtitle: 'Follow these steps for a safe and efficient installation of your JH Heatsup.',
    },
    safety: {
      title: 'Safety First',
      description:
        'Installation must be performed by a qualified electrician in accordance with local electrical codes. Always ensure the power supply is switched off at the main panel before starting installation.',
    },
    toolsTitle: 'Tools Required',
    tools: {
      electricDrill: 'Electric Drill',
      screwdriver: 'Screwdriver',
      level: 'Level',
      measuringTape: 'Measuring Tape',
      wireStrippers: 'Wire Strippers',
      ladder: 'Ladder',
    },
    stepsTitle: 'Installation Steps',
    steps: {
      step1: {
        title: 'Positioning & Measuring',
        description:
          'Choose a location on the ceiling or high wall. For optimal performance, the heater should be installed 2.2m to 2.5m above the floor. Mark the drilling points according to the mounting bracket dimensions provided in the box.',
        bullet1: 'Maintain minimum 0.5m distance from side walls.',
        bullet2: 'Ensure minimum 0.2m distance from the ceiling (if wall mounted).',
      },
      step2: {
        title: 'Mounting the Brackets',
        description:
          'Drill holes at the marked positions and insert the expansion bolts. Securely fasten the mounting brackets to the surface. Slide the heater\'s rear sliding brackets into the mounted brackets and tighten the adjusting screws to set the desired angle (0°, 15°, 30°, or 45°).',
      },
      step3: {
        title: 'Electrical Connection',
        description:
          'Connect the power cable to the mains supply. Ensure proper grounding. For units with remote control, ensure the receiver eye is visible for the remote to work effectively.',
        wireLive: 'L — Live',
        wireLiveColor: 'Brown',
        wireNeutral: 'N — Neutral',
        wireNeutralColor: 'Blue',
        wireEarth: 'E — Earth',
        wireEarthColor: 'Yellow / Green',
      },
      step4: {
        title: 'Testing',
        description:
          'Switch on the power. The heater should reach full operating temperature within 5 minutes. You may notice a slight smell during the first use; this is normal and will dissipate quickly.',
      },
    },
    download: {
      title: 'View Product Manual',
      subtitle: 'Access the complete installation manual with diagrams for your specific model.',
      jhnr: 'View Manual',
      industrial: 'View Manual',
    },
  },
  warrantyPolicy: {
    header: {
      title: 'Warranty Policy',
      subtitle: 'Our commitment to quality and your peace of mind.',
    },
    overview: {
      standardCoverage: 'Standard Coverage',
      standardCoverageDesc:
        'JH Heatsup products are warranted to be free from defects in materials and workmanship under normal use.',
      warrantyPeriod: 'Warranty Period',
      residentialPeriod: '2 Years for residential heaters.',
      industrialPeriod: '1 Year for industrial heaters and controllers.',
    },
    sections: {
      scope: {
        title: '1. Warranty Scope',
        description:
          'This warranty applies to the original purchaser of the product and is non-transferable. It covers the repair or replacement of any part of the product that proves to be defective in material or workmanship within the warranty period.',
      },
      notCovered: {
        title: '2. What is Not Covered?',
        description: 'This warranty does not cover damage resulting from:',
        item1: 'Improper installation not in accordance with the provided manual.',
        item2: 'Misuse, abuse, negligence, or accident.',
        item3: 'Alteration or modification of the product.',
        item4: 'Power surges or lightning strikes.',
        item5: 'Normal wear and tear (e.g., surface discoloration over time).',
      },
      claim: {
        title: '3. How to Make a Claim',
        description: 'To obtain warranty service, please contact our support team with the following information:',
        proofOfPurchase: 'Proof of purchase (Invoice/Receipt)',
        defectDescription: 'Description and photos/video of the defect',
        serialNumber: 'Serial number of the unit',
        contactEmail: 'Contact Email',
      },
    },
  },
};
